import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from '@mui/material';
import { Assessment as AssessmentIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useAccountsBase } from './voucherShared';
import { useDebounced } from './AccountsFilters';
import { formatMoney } from '../../services/expenseAccountsApi';

// Shared pieces of the Accounts reports (Phase 5B-1). Filtering, sorting, pagination,
// totals and exports all happen on the server; the browser only shows one page.

export const REPORT_TABS = [
  { value: '', label: 'Dashboard' },
  { value: 'payables', label: 'Payables / Outstanding' },
  { value: 'payments', label: 'Payment Register' },
  { value: 'wallets', label: 'Wallet Balances' },
  { value: 'advances', label: 'Advances & Settlements' },
  { value: 'bills', label: 'Bill Download' },
  { value: 'reminders', label: 'Reminders' },
];

export const useReportsBase = () => `${useAccountsBase()}/reports`;

export const ReportsHeader = ({ title, subtitle, actions }) => {
  const base = useReportsBase();
  const { pathname } = useLocation();
  const current = pathname.slice(base.length).replace(/^\//, '').split('/')[0];
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            {title}
          </Typography>
          {subtitle && <Typography variant="body1" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {actions}
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={REPORT_TABS.some((t) => t.value === current) ? current : false} variant="scrollable" scrollButtons="auto" aria-label="Accounts reports">
          {REPORT_TABS.map((t) => (
            <Tab key={t.value || 'dashboard'} value={t.value} label={t.label} component={RouterLink}
              to={t.value ? `${base}/${t.value}` : base} sx={{ textTransform: 'none' }} />
          ))}
        </Tabs>
      </Box>
    </>
  );
};

// Server-side report state: search, filters, sort, page; reloads when any of them change
export const useReport = (fetcher, { emptyFilters = {}, defaultSort, initialFilters } = {}) => {
  const [res, setRes] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim());
  const [filters, setFilters] = useState(initialFilters || emptyFilters);
  // Text filters (vendor name...) are typed: every filter is debounced like the search box
  const debouncedFilters = useDebounced(filters);
  const [sort, setSort] = useState(defaultSort);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(0); }, [debouncedSearch, debouncedFilters, sort]);

  const params = { search: debouncedSearch, ...debouncedFilters, ...sort };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRes(await fetcher({ ...params, page: page + 1, limit: rowsPerPage }));
      setError(null);
    } catch (e) {
      setRes(null);
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedFilters, sort, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  return {
    rows: res?.data || [],
    summary: res?.summary || [],
    response: res,
    total: res?.pagination?.totalItems || 0,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage: (n) => { setRowsPerPage(n); setPage(0); },
    search,
    setSearch,
    filters,
    setFilters,
    setFilter: (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value })),
    setFilterValue: (key) => (value) => setFilters((f) => ({ ...f, [key]: value })),
    sort,
    toggleSort: (key) => setSort((s) => ({ sort: key, order: s?.sort === key && s.order === 'desc' ? 'asc' : 'desc' })),
    clear: () => { setSearch(''); setFilters(emptyFilters); },
    loading,
    error,
    params,
    reload: load,
  };
};

// CSV export of the current server-side filters; errors (e.g. too large) are shown
export const ExportButton = ({ onExport, params, label = 'Export (CSV)' }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onExport(params);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={run} disabled={busy} sx={{ textTransform: 'none' }}>
      {label}
    </Button>
  );
};

// One block per currency; `lines(s)` returns the text lines of a block. Never a cross-currency total.
export const CurrencyTotals = ({ summary, title, lines, testId = 'currency-totals' }) => {
  if (!summary?.length) return null;
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }} data-testid={testId}>
      {summary.map((s) => (
        <Paper key={s.currency} variant="outlined" sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title(s)}</Typography>
          {lines(s).filter(Boolean).map((l) => (
            <Typography key={l} variant="caption" color="text.secondary" sx={{ display: 'block' }}>{l}</Typography>
          ))}
        </Paper>
      ))}
    </Stack>
  );
};

// Actual money positions from the wallet ledger, per currency (Phase 5B-1A): a positive
// wallet is recoverable from the employee, a negative wallet is payable to the employee.
export const WalletPositions = ({ positions, testId = 'wallet-positions' }) => (
  <CurrencyTotals
    testId={testId}
    summary={positions}
    title={(p) => `${p.currency} wallets (actual)`}
    lines={(p) => [
      `Recoverable from employees ${formatMoney(p.recoverable, p.currency)} (${p.recoverableEmployees})`,
      `Payable to employees ${formatMoney(p.payable, p.currency)} (${p.payableEmployees})`,
    ]}
  />
);

// Wallet report link opening the ledger of one employee + currency
export const walletLedgerLink = (reportsBase, employee, currency) => `${reportsBase}/wallets?${new URLSearchParams({ employeeId: String(employee), currency: currency || '' })}`;

// columns: [{ label, sortKey?, render(row), nowrap?, align? }]
export const ReportTable = ({ report, columns, rowKey = (r) => r._id, onRowClick, emptyText = 'Nothing matches the filters.', rowsPerPageOptions = [10, 25, 50, 100], before }) => {
  const { rows, loading, error, total, page, setPage, rowsPerPage, setRowsPerPage, sort, toggleSort } = report;
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error.message}</Alert>}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {before && <TableCell padding="checkbox" sx={{ color: 'white' }}>{before.header}</TableCell>}
              {columns.map((c) => (
                <TableCell key={c.label} align={c.align} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {c.sortKey ? (
                    <TableSortLabel
                      active={sort?.sort === c.sortKey}
                      direction={sort?.sort === c.sortKey ? sort.order : 'desc'}
                      onClick={() => toggleSort(c.sortKey)}
                      sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                    >
                      {c.label}
                    </TableSortLabel>
                  ) : c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length + (before ? 1 : 0)} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + (before ? 1 : 0)} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">{error ? 'No data.' : emptyText}</Typography></TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={rowKey(row)} hover={Boolean(onRowClick)} sx={{ cursor: onRowClick ? 'pointer' : 'default' }} onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {before && <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>{before.cell(row)}</TableCell>}
                {columns.map((c) => (
                  <TableCell key={c.label} align={c.align} sx={c.nowrap ? { whiteSpace: 'nowrap' } : undefined}>{c.render(row)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </TableContainer>
    </>
  );
};

export const FilterBar = ({ children, onClear }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
    {children}
    {onClear && <Button onClick={onClear} sx={{ textTransform: 'none' }}>Clear filters</Button>}
  </Box>
);
